<template>
    <v-dialog v-if="canCreateCredit" v-model="createCreditDialog" max-width="500" persistent>
        <template v-slot:activator="{ on, attrs }">
            <div class="d-flex align-center flex-column">
                <v-btn v-bind="attrs" v-on="on" large fab elevation="0" aria-label="Create credit">
                    <v-icon large>mdi-plus</v-icon>
                </v-btn>
                <span class="mt-2">Create credit</span>
            </div>
        </template>
        <v-card flat>
            <v-card-title class="secondary--text flex-nowrap align-start">
                Create credit
                <v-spacer></v-spacer>
                <v-btn icon :disabled="creatingCredit" aria-label="Close" @click="createCreditDialog = false">
                    <v-icon>clear</v-icon>
                </v-btn>
            </v-card-title>
            <v-card-text>
                <v-form @submit.prevent="createCredit">
                    <v-text-field
                        v-model="creditsToCreate"
                        v-mask="currencyMask"
                        label="Amount"
                        prefix="AC"
                        outlined
                        dense
                        :disabled="creatingCredit"
                        :rules="[value => Number(value.replace(/,/g, '')) > 0 || 'Enter a positive amount.']"></v-text-field>
                    <v-textarea
                        v-model="creditComment"
                        label="Comment"
                        outlined
                        dense
                        rows="3"
                        :disabled="creatingCredit"
                        :rules="[value => !!value.trim() || 'A comment is required.']"></v-textarea>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" text :loading="creatingCredit" :disabled="!canSubmitCredit" @click="createCredit">Create credit</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { mapState } from 'vuex'
import { v4 as uuidv4 } from 'uuid'

const currencyMask = createNumberMask({
    prefix: '',
    allowDecimal: true
})

export default {
    name: 'CreateCreditDialog',
    data() {
        return {
            currencyMask,
            createCreditDialog: false,
            creditsToCreate: '',
            creditComment: '',
            creatingCredit: false,
            creditCreationRequest: null
        }
    },
    computed: {
        ...mapState('userStore', ['rpManager', 'selectedRP']),
        canCreateCredit() {
            return this.rpManager && this.selectedRP?.parent_rpid === null && Number(this.selectedRP.rpid) === Number(this.$route.params.rpid)
        },
        creditAmount() {
            return Number(this.creditsToCreate.replace(/,/g, ''))
        },
        canSubmitCredit() {
            return this.canCreateCredit && !this.creatingCredit && Number.isFinite(this.creditAmount) && this.creditAmount > 0 && !!this.creditComment.trim()
        }
    },
    methods: {
        async createCredit() {
            if (!this.canSubmitCredit) return
            const rpid = this.$route.params.rpid
            const amount = this.creditAmount
            const transactionComment = this.creditComment.trim()
            const previous = this.creditCreationRequest
            if (!previous || previous.rpid !== rpid || previous.amount !== amount || previous.transaction_comment !== transactionComment) {
                this.creditCreationRequest = { rpid, amount, transaction_comment: transactionComment, idempotency_key: uuidv4() }
            }
            const { idempotency_key: idempotencyKey } = this.creditCreationRequest
            this.creatingCredit = true
            try {
                await this.$axios.post(`/resource_pools/${rpid}/credits`, {
                    amount,
                    transaction_comment: transactionComment,
                    idempotency_key: idempotencyKey
                })
                this.createCreditDialog = false
                this.creditsToCreate = ''
                this.creditComment = ''
                this.creditCreationRequest = null
                this.$emit('credit-created')
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Credit created successfully.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Could not create credit. Please try again.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            } finally {
                this.creatingCredit = false
            }
        }
    }
}
</script>
